import * as React from "react";
import { Button } from "react-ui";

import { emptyRef } from "../../TypingUtils";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { TourTargetElement } from "../../Internals/TourManagement/TourTargetElement";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { useCandyTheme } from "../../Internals/ThemeProvider/CandyTheme/UseCandyTheme";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { Icon } from "../../Icons/Icon";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { ADD_ROW_BUTTON_CLICK } from "../../metricsAction";

import { AddRowButtonTestPageObject } from "./AddRowButtonTestPageObject";
import { Gap, ValidationText } from "./AddRowButton.styles";

export type RowButtonType = "link" | "button";

export interface AddRowButtonProps extends ControlValidationProps {
    onAddInstance: () => void;
    bindingPath: AbsoluteModelPath;
    disabled?: boolean;
    text?: string | boolean;
    type?: RowButtonType;
    isReadOnly?: boolean;
    "data-tid"?: string;
}

export const AddRowButton = (props: AddRowButtonProps) => {
    if (props.isReadOnly) {
        return <></>;
    }
    return <InteractiveButton {...props} />;
};

const InteractiveButton = (props: AddRowButtonProps) => {
    const track = useMetrics();
    const button = React.useRef<Button>(emptyRef);
    const [validationFocused, setValidationFocused] = React.useState(false);
    const { disabled, text = "Добавить строку", type = "link", bindingPath, onAddInstance, error, warning } = props;
    const theme = useCandyTheme();
    const validationText = disabled ? null : (error ?? warning);

    const handleClick = () => {
        track(ADD_ROW_BUTTON_CLICK, { path: bindingPath, type: type });
        onAddInstance();
    };

    useTestPageObject(() => AddRowButtonTestPageObject(props.onAddInstance), [onAddInstance]);

    const handleRequestFocus = () => {
        setValidationFocused(true);
        setTimeout(() => setValidationFocused(false), 2000);
    };

    return (
        <FocusManagementObserver onRequestFocus={handleRequestFocus}>
            <TourTargetElement controlId={`add_row_button_${bindingPath.toLegacyPath()}`}>
                <>
                    <Button
                        data-tid={props["data-tid"]}
                        data-focus="noFocus"
                        disabled={disabled}
                        use={type === "button" ? "default" : type}
                        onClick={handleClick}
                        ref={button}>
                        <span>
                            <Icon name="PlusIcon" size={theme.typography.primary.fontSize.getNumber()} /> {text}
                        </span>
                    </Button>
                    {validationText && (
                        <Gap>
                            <ValidationText focused={validationFocused}>{validationText}</ValidationText>
                        </Gap>
                    )}
                </>
            </TourTargetElement>
        </FocusManagementObserver>
    );
};
